import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";

const fisDocRecService = new EntityService("FisDocRec");
const fisDocRecItemService = new EntityService("FisDocRecItem");

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

// Busca todos os itens e retorna mapa { itemId -> cfop }
const buscarMapaCfop = async () => {
  try {
    const response = await fisDocRecItemService.getAll(1, 10000, "", "", null, null, null, null);
    const lista = response?.data?.data?.data;
    if (!Array.isArray(lista)) return {};
    const mapa = {};
    for (const item of lista) {
      const id = item._id || item.id;
      if (id) {
        mapa[id] = item.CFOP || item.cFOP || item.cfop || null;
      }
    }
    return mapa;
  } catch {
    return {};
  }
};

const resolverCfop = (linha, mapaCfop) => {
  // Se a própria linha já trouxer CFOP (view), usa direto
  const cfopDireto =
    linha.cfop ||
    linha.CFOP ||
    linha.cFOP ||
    linha.cfopNota ||
    linha.cfopItem ||
    null;

  if (cfopDireto && String(cfopDireto).trim() !== "-") {
    return String(cfopDireto).trim();
  }

  // Tenta pegar do primeiro item listado em fisDocRecItemS
  const ids = linha.fisDocRecItemS || linha.fisDocRecItems || [];
  if (Array.isArray(ids)) {
    for (const id of ids) {
      if (typeof id === "object" && id !== null) {
        const cfopItemDireto = id.CFOP || id.cFOP || id.cfop || null;
        if (cfopItemDireto) return String(cfopItemDireto).trim();
      }

      const rawId = typeof id === "object" ? (id._id || id.id) : id;
      const cfop = rawId ? mapaCfop[rawId] : null;
      if (cfop) return cfop;
    }
  }
  return "-";
};

const normalizarLinha = (linha = {}, mapaCfop = {}) => {
  const vlrICMS = toNumber(linha.vlrICMS || linha.icms);
  const vlrIPI = toNumber(linha.vlrIPI || linha.ipi);
  const vlrPIS = toNumber(linha.vlrPIS || linha.pis);
  const vlrCOFINS = toNumber(linha.vlrCOFINS || linha.cofins);
  const vlrISS = toNumber(linha.vlrISS || linha.iss);
  const vlrICMSST = toNumber(linha.vlrICMSST || linha.icmsST || linha.vICMSST);
  const vlrOutrosImpostos = toNumber(
    linha.vlrOutrosImpostos ||
    linha.outrosImpostos ||
    linha.vlrTributos ||
    linha.tributos
  );

  return {
    status: linha.status || linha.situacao || "-",
    data: linha.dataEmissao || linha.data || linha.emissao || null,
    serie: linha.serie || "-",
    numero: linha.numero || linha.notaNumero || "-",
    cnpj: linha.emit_cnpj || linha.cnpj || linha.fornecedorCnpj || "-",
    razaoSocial: linha.emit_razao || linha.razaoSocial || linha.fornecedorNome || "-",
    cfop: resolverCfop(linha, mapaCfop),
    valorTotal: toNumber(linha.vlrFinanceiro || linha.vlrNF || linha.valorTotal || linha.valor || 0),
    icms: vlrICMS,
    ipi: vlrIPI,
    pis: vlrPIS,
    cofins: vlrCOFINS,
    iss: vlrISS,
    icmsSt: vlrICMSST,
    outrosImpostos: vlrOutrosImpostos,
    impostos: vlrICMS + vlrIPI + vlrPIS + vlrCOFINS + vlrISS + vlrICMSST + vlrOutrosImpostos
  };
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data);
};

const listarPorEntidade = async () => {
  const response = await fisDocRecService.getAll(1, 5000, "", "", null, null, null, "dataEmissao");
  return extrairLista(response);
};

const listarPorView = async (params = []) => {
  const viewNames = [
    "relatorio-nota-fiscal-recebida",
    "706bf457-c604-43e3-97f6-683d503f6a07",
    "fisdocrec"
  ];

  for (const viewName of viewNames) {
    const response = await viewService.viewRun(viewName, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  }

  return [];
};

export default {
  async listarNFRecebida(filtros = {}) {
    const params = [];

    if (filtros.dataInicio) {
      params.push({ dataType: "String", name: "dataInicio", value: filtros.dataInicio });
    }

    if (filtros.dataFim) {
      params.push({ dataType: "String", name: "dataFim", value: filtros.dataFim });
    }

    if (filtros.status) {
      params.push({ dataType: "String", name: "status", value: filtros.status });
    }

    if (filtros.fornecedor) {
      params.push({ dataType: "String", name: "fornecedor", value: filtros.fornecedor });
    }

    const [lista, mapaCfop] = await Promise.all([
      listarPorView(params).then(async (l) => {
        if (!l.length) return listarPorEntidade();
        return l;
      }),
      buscarMapaCfop()
    ]);

    return lista.map((linha) => normalizarLinha(linha, mapaCfop));
  }
};
